import React, { useState, useEffect, useCallback } from 'react';
import { 
  Typography, 
  Box, 
  Paper,
  Button,
  Pagination
} from '@mui/material';
import { authGet } from '../../services/apiService';
import FilterPanel from '../common/FilterPanel';
import Loading from '../common/Loading';
import BugsTable from './BugsTable';

/**
 * ProductionBugsView component for displaying production bugs
 * @returns {JSX.Element} ProductionBugsView component
 */
const ProductionBugsView = () => {
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 500; // Fixed page size of 500
  
  // Fetch production bugs when filters or page changes
  useEffect(() => {
    const fetchProductionBugs = async () => {
      try {
        setLoading(true);
        
        // Build query parameters
        const queryParams = new URLSearchParams();
        
        if (filters.state) {
          queryParams.append('state', filters.state);
        }
        
        if (filters.priority) {
          queryParams.append('priority', filters.priority);
        }
        
        if (filters.team) {
          queryParams.append('team', filters.team);
        }
        
        if (filters.fixVersion) {
          queryParams.append('fixVersion', filters.fixVersion);
        }
        
        if (startDate) {
          queryParams.append('startDate', startDate);
        }
        
        if (endDate) {
          queryParams.append('endDate', endDate);
        }
        
        // Add pagination parameters
        queryParams.append('page', page);
        queryParams.append('pageSize', pageSize);
        
        // Use the API service to get production bugs with filters and pagination
        const response = await authGet(`/api/workitems/production-bugs?${queryParams.toString()}`);
        
        if (response.success) {
          setBugs(response.data || []);
          setTotalCount(response.count || 0);
          setTotalPages(response.totalPages || 1);
          setError(null);
          console.log(`Fetched ${response.data?.length || 0} production bugs (page ${response.page} of ${response.totalPages})`);
        } else {
          throw new Error(response.message || 'Failed to fetch bugs');
        }
      } catch (err) {
        console.error('Error fetching production bugs:', err);
        setError('Failed to load production bugs. Please try again later.');
        setBugs([]);
        setTotalCount(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProductionBugs();
  }, [filters, startDate, endDate, page, pageSize]);
  
  
  // Handle filter changes
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);
  
  // Handle date changes
  const handleStartDateChange = useCallback((newDate) => {
    setStartDate(newDate);
  }, []);
  
  const handleEndDateChange = useCallback((newDate) => {
    setEndDate(newDate);
  }, []);
  
  // Handle refresh button click
  const handleRefresh = useCallback(() => {
    setLoading(true);
    // Reset to page 1 when refreshing
    setPage(1);
    
    // Re-fetch the data
    const fetchData = async () => {
      try {
        // Build query parameters
        const queryParams = new URLSearchParams();
        
        if (filters.state) {
          queryParams.append('state', filters.state);
        }
        
        if (filters.priority) {
          queryParams.append('priority', filters.priority);
        }
        
        if (filters.team) {
          queryParams.append('team', filters.team);
        }
        
        if (filters.fixVersion) {
          queryParams.append('fixVersion', filters.fixVersion);
        }
        
        if (startDate) {
          queryParams.append('startDate', startDate);
        }
        
        if (endDate) {
          queryParams.append('endDate', endDate);
        }
        
        // Add pagination parameters
        queryParams.append('page', 1); // Reset to page 1
        queryParams.append('pageSize', pageSize);
        
        const response = await authGet(`/api/workitems/production-bugs?${queryParams.toString()}`);
        
        if (response.success) {
          setBugs(response.data || []);
          setTotalCount(response.count || 0);
          setTotalPages(response.totalPages || 1);
          setError(null);
          console.log(`Refreshed ${response.data?.length || 0} production bugs (page 1 of ${response.totalPages})`);
        } else {
          throw new Error(response.message || 'Failed to refresh bugs');
        }
      } catch (err) {
        console.error('Error refreshing production bugs:', err);
        setError('Failed to refresh production bugs. Please try again later.');
        setBugs([]);
        setTotalCount(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [filters, startDate, endDate, pageSize]);
  
  // Handle page change
  const handlePageChange = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);
  
  
  // Render bugs view
  const renderBugsView = () => (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box mb={2}>
        <Typography variant="body1" color="textSecondary">
          View and filter production bugs reported in Azure DevOps
        </Typography>
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '8px' }}>Filters</span>
        </Typography>
        
        <FilterPanel 
          onFilterChange={handleFilterChange}
          showTeamFilter={true}
          showPriorityFilter={true}
          showStateFilter={true}
          showFixVersionFilter={true}
          showDateFilters={true}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          data={bugs}
        />
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1">
          {bugs.length} {bugs.length === 1 ? 'Bug' : 'Bugs'} found
        </Typography>
        
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh Bugs
        </Button>
      </Box>
      
      {loading ? (
        <Loading message="Loading production bugs..." />
      ) : error ? (
        <Paper elevation={2} style={{ padding: 16, marginTop: 16 }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      ) : (
        <>
          <BugsTable bugs={bugs} />
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
              <Typography variant="body2" sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                Showing {bugs.length} of {totalCount} bugs (Page {page} of {totalPages})
              </Typography>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={handlePageChange} 
                color="primary" 
                showFirstButton 
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
  
  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Production Bugs
        </Typography>
        <Typography variant="body1" color="textSecondary">
          View and filter production bugs reported in Azure DevOps
        </Typography>
      </Box>
      
      {renderBugsView()}
    </Box>
  );
};

export default ProductionBugsView;
